import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from './services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is logged in on mount
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        setLoading(false);
        // Note: Token validation happens automatically via axios interceptor
        // If token is invalid, user will be redirected on the next API call
      } catch (err) {
        logout();
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const register = async (userData) => {
    try {
      setError(null);
      const response = await authAPI.register(userData);
      // Registration now returns a message + email (no auto-login)
      // User must verify email first
      return { 
        success: true, 
        needsVerification: true,
        email: response.data.email,
        message: response.data.message,
      };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const login = async (credentials) => {
    try {
      setError(null);
      const response = await authAPI.login(credentials);
      const { access_token, module, user_type, module_status } = response.data;
      
      localStorage.setItem('token', access_token);
      
      // Get full user profile
      const userResponse = await authAPI.getCurrentUser();
      const fullUser = userResponse.data;
      setUser(fullUser);
      localStorage.setItem('user', JSON.stringify(fullUser));
      
      // Return routing hint so LoginPage can redirect correctly
      return { success: true, module, user_type, module_status };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setError(null);
  };

  /**
   * Establish session from a partner SSO token exchange (TwinGP).
   * Stores JWT, loads /me, and updates auth state.
   */
  const setSessionFromPartner = async (accessToken) => {
    localStorage.setItem('token', accessToken);
    const userResponse = await authAPI.getCurrentUser();
    const fullUser = userResponse.data;
    setUser(fullUser);
    localStorage.setItem('user', JSON.stringify(fullUser));
    return fullUser;
  };

  /**
   * Refresh user data from the backend (e.g. after payment activates internship).
   * Returns the updated user object.
   */
  const refreshUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      const freshUser = response.data;
      setUser(freshUser);
      localStorage.setItem('user', JSON.stringify(freshUser));
      return freshUser;
    } catch (err) {
      // If token is invalid, the interceptor will handle redirect
      return null;
    }
  };

  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    refreshUser,
    setSessionFromPartner,
    isAuthenticated: !!user,
    isAdmin: user?.user_type === 'admin',
    isInternship: user?.module === 'internship',
    isCorporate: user?.user_type === 'corporate',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
